// src/pages/CoinsHistory.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaSearch, FaCoins } from 'react-icons/fa';
import { API_BASE } from '../utils/helpers';

const LOYALTY_ENDPOINT = `${API_BASE}/api/admin/users-loyalty-stats`;

export default function CoinsHistory() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { fetchStats(1); }, []);

  useEffect(() => {
    let data = [...users];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter((u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        String(u.id || '').includes(searchTerm) ||
        String(u.shopify_id || '').includes(searchTerm)
      );
    }
    setFilteredUsers(data);
  }, [searchTerm, users]);

  const fetchStats = async (page = 1) => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(LOYALTY_ENDPOINT, {
        params: { page },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const apiUsers = res.data?.users || [];
      setUsers(apiUsers);
      setFilteredUsers(apiUsers);
      setCurrentPage(res.data?.pagination?.page || page);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Failed to load loyalty stats:', err);
      setError('Failed to load users loyalty stats.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dt) => {
    if (!dt) return '—';
    try { return new Date(dt).toLocaleString(); } catch { return String(dt); }
  };
  const asNumber = (val) => { const n = Number(val || 0); return Number.isFinite(n) ? n : 0; };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Points History</h2>
        <p className="text-sm text-slate-500">Loyalty points balances per customer.</p>
      </div>

      {error && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">{error}</div>}

      <div className="ui-card mb-5 p-4">
        <div className="relative max-w-md">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
          <input
            className="ui-input pl-9"
            placeholder="Search by name, email, user ID or Shopify ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Shopify ID</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Total Points</th>
                <th>Available</th>
                <th>Redeemed</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j}><div className="h-4 w-full max-w-[110px] animate-pulse rounded bg-slate-100" /></td>
                    ))}
                  </tr>
                ))}

              {!loading && filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
                      <FaCoins className="text-2xl" />
                      <p className="text-sm">No records found.</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filteredUsers.map((u) => {
                const available = asNumber(u.available_balance);
                const redeemed = asNumber(u.total_redeemed);
                const totalPoints = available + redeemed;
                return (
                  <tr key={u.id}>
                    <td className="text-slate-400">{u.id}</td>
                    <td className="font-mono text-xs text-slate-500">{u.shopify_id || '—'}</td>
                    <td className="font-medium text-slate-900">{u.name || '—'}</td>
                    <td className="text-slate-500">{u.email || '—'}</td>
                    <td className="font-medium text-slate-900">{totalPoints.toFixed(2)}</td>
                    <td><span className="ui-badge bg-emerald-50 text-emerald-700">{available.toFixed(2)}</span></td>
                    <td><span className="ui-badge bg-slate-100 text-slate-600">{redeemed.toFixed(2)}</span></td>
                    <td className="whitespace-nowrap text-slate-500">{formatDate(u.shopify_created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Compact pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
            <button
              className="ui-btn-ghost disabled:opacity-40"
              disabled={currentPage <= 1}
              onClick={() => currentPage > 1 && fetchStats(currentPage - 1)}
            >
              Previous
            </button>
            <span className="text-sm text-slate-500">Page {currentPage} of {totalPages}</span>
            <button
              className="ui-btn-ghost disabled:opacity-40"
              disabled={currentPage >= totalPages}
              onClick={() => currentPage < totalPages && fetchStats(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
